import { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/ApiService';

export interface Favorito {
  favorito_id: string;
  adicionado_em: string;
  imovel: any; // Tipo do imóvel conforme estrutura do backend
  imovel_id?: string; // ID do imóvel (pode estar aqui ou em imovel.id)
}

interface FavoritosResponse {
  success: boolean;
  message: string;
  data?: {
    total: number;
    favoritos: Favorito[];
  };
}

const FAVORITOS_CACHE_KEY = 'favoritosCache';
const FAVORITOS_TIMESTAMP_KEY = 'favoritosTimestamp';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

export const useFavoritos = (clienteId: string | null) => {
  const [favoritos, setFavoritos] = useState<Favorito[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cache de favoritos
  const getFavoritosCache = (): Favorito[] | null => {
    const timestamp = localStorage.getItem(FAVORITOS_TIMESTAMP_KEY);
    if (!timestamp) return null;

    const cacheAge = Date.now() - parseInt(timestamp);
    if (cacheAge > CACHE_DURATION) {
      // Cache expirado
      localStorage.removeItem(FAVORITOS_CACHE_KEY);
      localStorage.removeItem(FAVORITOS_TIMESTAMP_KEY);
      return null;
    }

    const cached = localStorage.getItem(FAVORITOS_CACHE_KEY);
    return cached ? JSON.parse(cached) : null;
  };

  const setFavoritosCache = (favoritos: Favorito[]) => {
    localStorage.setItem(FAVORITOS_CACHE_KEY, JSON.stringify(favoritos));
    localStorage.setItem(FAVORITOS_TIMESTAMP_KEY, Date.now().toString());
  };

  // Listar favoritos
  const listarFavoritos = useCallback(async () => {
    if (!clienteId) {
      setFavoritos([]);
      setTotal(0);
      return;
    }

    // Verificar cache primeiro (mas sempre recarregar para garantir dados atualizados)
    // Usar cache apenas se tiver menos de 30 segundos
    const cached = getFavoritosCache();
    const cacheTimestamp = localStorage.getItem(FAVORITOS_TIMESTAMP_KEY);
    if (cached && cacheTimestamp) {
      const cacheAge = Date.now() - parseInt(cacheTimestamp);
      // Usar cache apenas se tiver menos de 30 segundos
      if (cacheAge < 30000) {
        console.log('📋 Usando cache de favoritos:', { total: cached.length, age: cacheAge });
        setFavoritos(cached);
        setTotal(cached.length);
        // Recarregar em background para atualizar
        setTimeout(() => {
          listarFavoritos();
        }, 100);
        return;
      }
    }

    setLoading(true);
    setError(null);

    try {
      console.log('📋 Buscando favoritos da API:', clienteId);
      const response = await apiService.listarFavoritos(clienteId) as FavoritosResponse;

      if (!response.success) {
        throw new Error(response.message || 'Erro ao listar favoritos');
      }

      const favoritosList = response.data?.favoritos || [];
      console.log('✅ Favoritos recebidos:', { total: favoritosList.length, favoritos: favoritosList });
      setFavoritos(favoritosList);
      setTotal(response.data?.total || 0);

      // Atualizar cache
      setFavoritosCache(favoritosList);
    } catch (err: any) {
      const errorMessage = err?.message || 'Erro ao listar favoritos';
      setError(errorMessage);
      console.error('Erro ao listar favoritos:', err);
    } finally {
      setLoading(false);
    }
  }, [clienteId]);

  // Adicionar favorito
  const adicionarFavorito = useCallback(async (imovelId: string) => {
    if (!clienteId) {
      throw new Error('Cliente não identificado. Por favor, faça seu cadastro primeiro.');
    }

    setLoading(true);
    setError(null);

    try {
      // Invalidar cache antes de adicionar
      localStorage.removeItem(FAVORITOS_CACHE_KEY);
      localStorage.removeItem(FAVORITOS_TIMESTAMP_KEY);

      const response = await apiService.adicionarFavorito(clienteId, imovelId);

      if (!response.success) {
        throw new Error(response.message || 'Erro ao adicionar favorito');
      }

      // Invalidar cache e atualizar lista de favoritos
      localStorage.removeItem(FAVORITOS_CACHE_KEY);
      localStorage.removeItem(FAVORITOS_TIMESTAMP_KEY);
      
      // Aguardar um pouco antes de recarregar para garantir que o backend processou
      await new Promise(resolve => setTimeout(resolve, 100));
      
      await listarFavoritos();

      return response;
    } catch (err: any) {
      const errorMessage = err?.message || 'Erro ao adicionar favorito';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [clienteId, listarFavoritos]);

  // Remover favorito
  const removerFavorito = useCallback(async (imovelId: string) => {
    if (!clienteId) {
      throw new Error('Cliente não identificado');
    }

    setLoading(true);
    setError(null);

    try {
      // Invalidar cache antes de remover
      localStorage.removeItem(FAVORITOS_CACHE_KEY);
      localStorage.removeItem(FAVORITOS_TIMESTAMP_KEY);

      const response = await apiService.removerFavorito(clienteId, imovelId);

      if (!response.success) {
        throw new Error(response.message || 'Erro ao remover favorito');
      }

      // Invalidar cache e atualizar lista de favoritos
      localStorage.removeItem(FAVORITOS_CACHE_KEY);
      localStorage.removeItem(FAVORITOS_TIMESTAMP_KEY);
      
      // Aguardar um pouco antes de recarregar para garantir que o backend processou
      await new Promise(resolve => setTimeout(resolve, 100));
      
      await listarFavoritos();

      return response;
    } catch (err: any) {
      const errorMessage = err?.message || 'Erro ao remover favorito';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [clienteId, listarFavoritos]);

  // Verificar se imóvel está nos favoritos
  const isFavorito = useCallback((imovelId: string): boolean => {
    if (!imovelId) return false;
    const isFav = favoritos.some((f) => {
      const favImovelId = f.imovel?.id || f.imovel_id;
      return favImovelId === imovelId;
    });
    console.log('🔍 Verificando se imóvel é favorito:', { imovelId, isFav, favoritos: favoritos.length });
    return isFav;
  }, [favoritos]);

  // Toggle favorito (adicionar ou remover)
  const toggleFavorito = useCallback(async (imovelId: string) => {
    if (isFavorito(imovelId)) {
      await removerFavorito(imovelId);
    } else {
      await adicionarFavorito(imovelId);
    }
  }, [isFavorito, adicionarFavorito, removerFavorito]);

  // Carregar favoritos ao montar ou quando clienteId mudar
  useEffect(() => {
    if (clienteId) {
      listarFavoritos();
    } else {
      setFavoritos([]);
      setTotal(0);
    }
  }, [clienteId, listarFavoritos]);

  return {
    favoritos,
    total,
    loading,
    error,
    listarFavoritos,
    adicionarFavorito,
    removerFavorito,
    toggleFavorito,
    isFavorito
  };
};

